import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";

const DOMAIN_RULES = {
  minLength: 4,
  validExample: "https://bitcryptogateway.xyz",
  blocked: [""],
};

const validateDomain = (input) => {
  const value = input.trim();

  if (!value) {
    return { valid: false, error: "Domain cannot be empty.", suggestion: null };
  }

  // Must start with https://
  if (value.startsWith("http://")) {
    return {
      valid: false,
      error: "Domain must use HTTPS, not HTTP.",
      suggestion: value.replace("http://", "https://"),
    };
  }

  if (!value.startsWith("https://")) {
    return {
      valid: false,
      error: "Domain must start with https://",
      suggestion: `https://${value.replace(/^\/+/, "")}`,
    };
  }

  // Remove protocol for further checks
  const withoutProtocol = value.replace("https://", "");

  // No trailing slashes
  if (withoutProtocol.endsWith("/")) {
    return {
      valid: false,
      error: "Remove the trailing slash (/) from the end.",
      suggestion: value.replace(/\/+$/, ""),
    };
  }

  // No paths allowed (e.g. https://domain.com/something)
  if (withoutProtocol.includes("/")) {
    return {
      valid: false,
      error: "Enter only the domain. Do not include any path after the domain.",
      suggestion: `https://${withoutProtocol.split("/")[0]}`,
    };
  }

  // Must contain at least one dot (e.g. domain.xyz)
  if (!withoutProtocol.includes(".")) {
    return {
      valid: false,
      error: "This does not look like a valid domain. It must contain a TLD (e.g. .com, .xyz).",
      suggestion: null,
    };
  }

  // No spaces
  if (withoutProtocol.includes(" ")) {
    return {
      valid: false,
      error: "Domain cannot contain spaces.",
      suggestion: `https://${withoutProtocol.replace(/\s+/g, "")}`,
    };
  }

  // Basic domain pattern check (letters, numbers, hyphens, dots)
  const domainPattern = /^[a-zA-Z0-9][a-zA-Z0-9.-]*\.[a-zA-Z]{2,}$/;
  if (!domainPattern.test(withoutProtocol)) {
    return {
      valid: false,
      error: "Domain contains invalid characters. Only letters, numbers, hyphens, and dots are allowed.",
      suggestion: null,
    };
  }

  // Block main Paycoinz domains
  if (DOMAIN_RULES.blocked.includes(withoutProtocol.toLowerCase())) {
    return {
      valid: false,
      error: "You cannot use a Paycoinz domain as the payment gateway domain. Use a separate domain.",
      suggestion: null,
    };
  }

  return { valid: true, error: null, suggestion: null };
};

const SystemSettings = () => {
  const [domain, setDomain] = useState("");
  const [savedDomain, setSavedDomain] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [validation, setValidation] = useState({ valid: true, error: null, suggestion: null });

  useEffect(() => {
    fetchDomain();
  }, []);

  const fetchDomain = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/settings/public/payment-domain`);
      if (response?.data?.domain) {
        setDomain(response.data.domain);
        setSavedDomain(response.data.domain);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDomainChange = (e) => {
    const newValue = e.target.value;
    setDomain(newValue);

    // Validate in real-time (only show errors after user types 3+ chars)
    if (newValue.trim().length > 3) {
      setValidation(validateDomain(newValue));
    } else {
      setValidation({ valid: true, error: null, suggestion: null });
    }
  };

  const applySuggestion = () => {
    if (validation.suggestion) {
      setDomain(validation.suggestion);
      setValidation(validateDomain(validation.suggestion));
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();

    const result = validateDomain(domain);
    setValidation(result);

    if (!result.valid) {
      toast.error(result.error);
      return;
    }

    const formattedDomain = domain.trim().replace(/\/+$/, "");

    setIsSaving(true);
    try {
      await axios.put(
        `${process.env.REACT_APP_API_URL}/settings/admin/payment_link_domain`,
        { value: formattedDomain },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      setDomain(formattedDomain);
      setSavedDomain(formattedDomain);
      toast.success("Payment Domain updated successfully!");
    } catch (error) {
      console.error(error);
      toast.error("Failed to update domain. Make sure you have Admin permissions.");
    } finally {
      setIsSaving(false);
    }
  };

  const hasChanged = domain.trim() !== savedDomain;

  return (
    <div>
      <div className="content-wrapper">
        <div className="content-header">
          <div className="container-fluid">
            <div className="row mb-2">
              <div className="col-sm-6">
                <h1 className="m-0">System Settings</h1>
              </div>
            </div>
          </div>
        </div>
        <section className="content">
          <div className="container-fluid">
            <div className="card card-primary">
              <div className="card-header">
                <h3 className="card-title">Payment Link Configuration</h3>
              </div>
              <form onSubmit={handleSave}>
                <div className="card-body">
                  <div className="form-group">
                    <label htmlFor="domainInput">
                      Custom Payment Domain URL <span style={{ color: "#dc3545" }}>*</span>
                    </label>
                    <input
                      id="domainInput"
                      type="text"
                      className={`form-control ${!validation.valid ? "is-invalid" : domain && validation.valid ? "is-valid" : ""}`}
                      value={domain}
                      onChange={handleDomainChange}
                      placeholder="e.g. https://bitcryptogateway.xyz"
                      disabled={isLoading}
                      autoComplete="off"
                    />

                    {/* Validation Error */}
                    {!validation.valid && (
                      <div className="invalid-feedback" style={{ display: "block" }}>
                        <i className="fas fa-exclamation-triangle" style={{ marginRight: 6 }}></i>
                        {validation.error}
                      </div>
                    )}

                    {/* Auto-fix Suggestion */}
                    {!validation.valid && validation.suggestion && (
                      <div
                        style={{
                          marginTop: 6,
                          padding: "8px 12px",
                          backgroundColor: "#fff3cd",
                          border: "1px solid #ffc107",
                          borderRadius: 4,
                          fontSize: 13,
                        }}
                      >
                        <strong>Did you mean:</strong>{" "}
                        <code style={{ color: "#155724", fontWeight: "bold" }}>{validation.suggestion}</code>
                        <button
                          type="button"
                          className="btn btn-sm btn-warning"
                          style={{ marginLeft: 12, padding: "2px 10px", fontSize: 12 }}
                          onClick={applySuggestion}
                        >
                          Apply Fix
                        </button>
                      </div>
                    )}

                    {/* Success indicator */}
                    {validation.valid && domain && (
                      <div className="valid-feedback" style={{ display: "block" }}>
                        <i className="fas fa-check-circle" style={{ marginRight: 6 }}></i>
                        Valid domain format
                      </div>
                    )}

                    {/* Help text */}
                    <small className="form-text text-muted" style={{ marginTop: 8 }}>
                      <strong>Required format:</strong> <code>https://yourdomain.com</code><br />
                      • Must start with <code>https://</code><br />
                      • No trailing slash, no paths, no spaces<br />
                      • Cannot be a Paycoinz domain<br />
                      • Example: <code>{DOMAIN_RULES.validExample}</code>
                    </small>

                    {/* Currently active domain */}
                    {savedDomain && (
                      <div
                        style={{
                          marginTop: 12,
                          padding: "8px 12px",
                          backgroundColor: "#d4edda",
                          border: "1px solid #28a745",
                          borderRadius: 4,
                          fontSize: 13,
                        }}
                      >
                        <i className="fas fa-globe" style={{ marginRight: 6 }}></i>
                        <strong>Currently Active:</strong> {savedDomain}
                      </div>
                    )}
                  </div>
                </div>
                <div className="card-footer">
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={isLoading || isSaving || !validation.valid || !hasChanged}
                  >
                    {isSaving ? (
                      <>
                        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" style={{ marginRight: 6 }}></span>
                        Saving...
                      </>
                    ) : (
                      "Save Domain"
                    )}
                  </button>
                  {!hasChanged && domain && (
                    <span className="text-muted" style={{ marginLeft: 12, fontSize: 13 }}>
                      No changes to save
                    </span>
                  )}
                </div>
              </form>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default SystemSettings;
